import { FC, useState, useEffect, useCallback } from 'react';

import { Cities } from '@/types/cities';
import { States } from '@/types/states';
import { Modalities } from '@/types/modalities';

import { DefaultValues, defaultValues } from '@/schemas/home';

import { Button, Form, Row, Col } from 'react-bootstrap';
import { Neighborhoods } from '@/types/neighborhoods';
import {
	getCities,
	getNeighborhoodsByCityId,
	getStates,
} from '@/services/api/locations';
import { getModalities } from '@/services/api/modalities';
import Link from 'next/link';

interface FormSearchProps {
	onSubmitForm: (fields: DefaultValues) => void;
}

export const FormSearch: FC<FormSearchProps> = ({ onSubmitForm }) => {
	const [dataNeighborhoods, setDataNeighborhoods] = useState<Neighborhoods[]>(
		[]
	);
	const [dataCities, setDataCities] = useState<Cities[]>([]);
	const [dataStates, setDataStates] = useState<States[]>([]);
	const [dataModalities, setDataModalities] = useState<Modalities[]>([]);

	const [formFields, setFormFields] = useState<DefaultValues>(defaultValues);

	const onGetAllNeighborhoods = useCallback(async () => {
		const indexCity = dataCities.findIndex(
			city => city.name === formFields.city
		);

		if (indexCity === -1) return;

		const response = await getNeighborhoodsByCityId(dataCities[indexCity]?.id);
		setDataNeighborhoods((response || []) as any);
	}, [dataCities, formFields.city]);

	const onGetAllCities = async () => {
		const response = await getCities(true);
		setDataCities((response || []) as any);
	};

	const onGetAllStates = async () => {
		const response = await getStates(true);
		setDataStates((response || []) as any);
	};

	const onGetAllModalities = async () => {
		const response = await getModalities(true);
		setDataModalities((response || []) as any);
	};

	useEffect(() => {
		onGetAllCities();
		onGetAllStates();
		onGetAllModalities();
	}, []);

	useEffect(() => {
		if (formFields.city) onGetAllNeighborhoods();
	}, [formFields.city, onGetAllNeighborhoods]);

	const onChangeFieldsValues = (field: string, value: string) => {
		if (field === 'cnpj' || field === 'phone') {
			value = value.replace(/\D/g, '');
		}
		setFormFields({
			...formFields,
			[field]: value,
		});
	};

	const onResetFieldsValues = () => {
		setFormFields(defaultValues);
		onSubmitForm(defaultValues);
	};

	function handleSubmit(e: { preventDefault: () => void }) {
		e.preventDefault();
		onSubmitForm(formFields);
	}

	return (
		<>
			<Row>
				<Col>
					<h1 className="mt-3 mb-3">Estabelecimentos</h1>
				</Col>
			</Row>
			<Row>
				<Col>
					<Form onSubmit={handleSubmit}>
						<Row className="g-3">
							<Col md={4}>
								<Form.Label htmlFor="name">Nome</Form.Label>
								<Form.Control
									id="name"
									type="text"
									placeholder="Nome"
									value={formFields.name}
									onChange={({ target }) =>
										onChangeFieldsValues('name', target.value)
									}
								/>
							</Col>
							<Col md={4}>
								<Form.Label htmlFor="phone">Telefone</Form.Label>
								<Form.Control
									id="phone"
									type="text"
									placeholder="Telefone"
									value={formFields.phone}
									onChange={({ target }) =>
										onChangeFieldsValues('phone', target.value)
									}
								/>
							</Col>
							<Col md={4}>
								<Form.Label htmlFor="cnpj">CNPJ</Form.Label>
								<Form.Control
									id="cnpj"
									type="text"
									placeholder="CNPJ"
									value={formFields.cnpj || ''}
									onChange={({ target }) =>
										onChangeFieldsValues('cnpj', target.value)
									}
								/>
							</Col>
						</Row>
						<Row className="g-3 mt-0">
							<Col md={4}>
								<Form.Label htmlFor="city">Cidade</Form.Label>
								<Form.Select
									id="city"
									aria-label="Cidade"
									value={formFields.city}
									onChange={({ target }) =>
										onChangeFieldsValues('city', target.value)
									}
									disabled={!dataCities.length}>
									<option value="">Selecionar</option>
									{dataCities?.map(({ name, id }) => (
										<option key={id} value={name}>
											{name}
										</option>
									))}
								</Form.Select>
							</Col>
							<Col md={4}>
								<Form.Label htmlFor="state">Estado</Form.Label>
								<Form.Select
									id="state"
									aria-label="Estado"
									value={formFields.state}
									onChange={({ target }) =>
										onChangeFieldsValues('state', target.value)
									}
									disabled={!dataStates?.length}>
									<option value="">Selecionar</option>
									{dataStates?.map(({ code, id, name }) => (
										<option key={id} value={name}>
											{code}
										</option>
									))}
								</Form.Select>
							</Col>
							<Col md={4}>
								<Form.Label htmlFor="neighborhood">Bairro</Form.Label>
								<Form.Select
									id="neighborhood"
									aria-label="Bairro"
									value={formFields.neighborhood}
									onChange={({ target }) =>
										onChangeFieldsValues('neighborhood', target.value)
									}
									disabled={!formFields.city}>
									<option value="">Selecionar</option>
									{dataNeighborhoods?.map(({ name, id }) => (
										<option key={id} value={name}>
											{name}
										</option>
									))}
								</Form.Select>
							</Col>
						</Row>
						<Row className="g-3 mt-0">
							<Col md={4}>
								<Form.Label htmlFor="modality">Modalidade</Form.Label>
								<Form.Select
									id="modality"
									aria-label="Modalidade"
									value={formFields.modality}
									onChange={({ target }) =>
										onChangeFieldsValues('modality', target.value)
									}
									disabled={!dataModalities.length}>
									<option value="">Selecionar</option>
									{dataModalities?.map(({ name, id }) => (
										<option key={id} value={name}>
											{name}
										</option>
									))}
								</Form.Select>
							</Col>
							<Col md={4}>
								<Form.Label htmlFor="establishmentIndicationCode">
									Código de Indicação
								</Form.Label>
								<Form.Control
									id="establishmentIndicationCode"
									type="text"
									placeholder="Código de Indicação"
									value={formFields.establishmentIndicationCode || ''}
									onChange={({ target }) =>
										onChangeFieldsValues(
											'establishmentIndicationCode',
											target.value
										)
									}
								/>
							</Col>
						</Row>
						<Row className="mt-4">
							<Col>
								<Button
									variant="primary"
									onClick={() => onSubmitForm(formFields)}>
									Buscar
								</Button>{' '}
								<Button variant="danger" onClick={onResetFieldsValues}>
									Limpar
								</Button>
							</Col>
							<Col style={{ display: 'flex', justifyContent: 'flex-end' }}>
								<Link href="/establishments/create">
									<Button variant="success">Cadastrar</Button>
								</Link>
							</Col>
						</Row>
					</Form>
				</Col>
			</Row>
		</>
	);
};

export default FormSearch;
